/**
 * HL Holdings Application
 * 메인 애플리케이션 클래스
 */

import { FamilySiteMenu } from "./components/FamilySiteMenu.js";
import { QuickMenu } from "./components/QuickMenu.js";
import { LanguageModal } from "./components/LanguageModal.js";
import { Drawer } from "./components/Drawer.js";
import { Header } from "./components/Header.js";
import { Popup } from "./components/Popup.js";
import { Pagination } from "./components/Pagination.js";
import { MainHero } from "./components/MainHero.js";
import { MainPofol } from "./components/MainPofol.js";
import { MainResource } from "./components/MainResource.js";
import { SubNav } from "./components/SubNav.js";
import { FloatMenu } from "./components/FloatMenu.js";
import { Captcha } from "./components/Captcha.js";
import { GoToTop } from "./components/GoToTop.js";
import { BaseFaq } from "./components/BaseFaq.js";
import { BeforeAfterFaq } from "./components/BeforeAfterFaq.js";
import { Marquee } from "./components/Marquee.js";
import { Inquiry } from "./components/Inquiry.js";
import { ResourcesDownloads } from "./components/ResourcesDownloads.js";
import { MainAnimation } from "./MainAnimation.js";
import { SubPage } from "./SubPage.js";
import { GlobalTransportPage } from "./GlobalTransportPage.js";

export class Application {
    constructor() {
        this.components = {};
        this.mainAnimation = new MainAnimation();
        this.subPage = new SubPage();
        this.globalTransportPage = new GlobalTransportPage();
        this.init();
    }

    init() {
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () =>
                this.initComponents(),
            );
        } else {
            this.initComponents();
        }
    }

    initComponents() {
        console.log("Application: Initializing components...");

        // Lenis 초기화
        this.initLenis();

        // 컴포넌트 초기화
        try {
            // LanguageModal은 가장 먼저 초기화 (다른 컴포넌트에서 참조)
            if (document.getElementById("language-modal")) {
                this.components.languageModal = new LanguageModal();
            }

            // 기본 컴포넌트
            this.components.pagination = new Pagination();

            // 조건부 컴포넌트
            if (document.getElementById("family-site-btn")) {
                this.components.familySiteMenu = new FamilySiteMenu();
            }

            if (document.getElementById("quick-menu")) {
                this.components.quickMenu = new QuickMenu();
            }

            if (document.getElementById("main-drawer")) {
                this.components.drawer = new Drawer();
            }

            if (document.getElementById("main-header")) {
                this.components.header = new Header();
            }

            if (document.querySelector(".main-popup-wrap")) {
                this.components.popup = new Popup();
            }

            // 기타 컴포넌트 (필요시)
            this.components.mainHero = new MainHero();
            this.components.mainPofol = new MainPofol();
            this.components.mainResource = new MainResource();
            this.components.subNav = new SubNav();
            this.components.floatMenu = new FloatMenu();
            this.components.captcha = new Captcha();
            this.components.goToTop = new GoToTop();
            this.components.baseFaq = new BaseFaq();
            this.components.beforeAfterFaq = new BeforeAfterFaq();
            this.components.marquee = new Marquee();
            this.components.inquiry = new Inquiry();

            if (document.querySelector(".no-resources")) {
                this.components.resourcesDownloads = new ResourcesDownloads();
            }
        } catch (error) {
            console.error("Application: Error initializing components", error);
        }

        // MainAnimation 초기화 (메인 페이지만)
        if (document.querySelector(".no-sub-hero") === null) {
            this.initMainAnimations();
        }

        // SubPage 초기화 (서브 페이지만)
        if (document.querySelector(".no-sub-hero")) {
            this.initSubPageAnimations();
        }

        AOS.init();
        this.initAosResilience();

        // 서브 페이지: ScrollTrigger pin 적용 후 AOS 재계산 + transport-module 내 컨텐츠 강제 노출 (중간에서 새로고침 시 미노출 방지)
        if (document.querySelector(".no-sub-hero")) {
            const runAfterLayout = () => {
                if (typeof ScrollTrigger !== "undefined")
                    ScrollTrigger.refresh();
                if (typeof AOS !== "undefined" && AOS.refresh) AOS.refresh();
                const section = document.getElementById(
                    "transport-module-section",
                );
                if (section) {
                    section
                        .querySelectorAll("[data-aos]")
                        .forEach((el) => el.classList.add("aos-animate"));
                }
            };
            requestAnimationFrame(() => {
                requestAnimationFrame(runAfterLayout);
            });
            if (document.getElementById("transport-module-section")) {
                setTimeout(runAfterLayout, 350);
            }
        }
    }

    /**
     * AOS가 "스크롤 위치 복원 상태에서 새로고침" 케이스를 놓쳐
     * 요소가 opacity:0 상태로 남는 문제 방지용 안전장치.
     */
    initAosResilience() {
        if (typeof AOS === "undefined") return;

        const safeRefresh = () => {
            try {
                if (AOS.refreshHard) AOS.refreshHard();
                else if (AOS.refresh) AOS.refresh();
            } catch (e) {
                // ignore
            }
        };

        // 1) 페이지 로드/복원 후 한 번 더 강제 refresh (스크롤 위치 복원 타이밍 대응)
        window.addEventListener("load", () => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    safeRefresh();
                    setTimeout(safeRefresh, 250);
                });
            });
        });
        window.addEventListener("pageshow", () => {
            setTimeout(safeRefresh, 50);
        });

        // 2) AOS가 누락하더라도 viewport에 들어오면 보이도록 보장
        const els = Array.from(document.querySelectorAll("[data-aos]"));
        if (!els.length || typeof IntersectionObserver === "undefined") return;

        const inView = (el) => {
            const r = el.getBoundingClientRect();
            const vh =
                window.innerHeight || document.documentElement.clientHeight;
            const vw =
                window.innerWidth || document.documentElement.clientWidth;
            return r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw;
        };

        // 초기 viewport도 즉시 노출(새로고침 위치가 아래일 수 있음)
        els.forEach((el) => {
            if (inView(el)) el.classList.add("aos-animate");
        });

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("aos-animate");
                    }
                });
            },
            { threshold: 0.01 },
        );

        els.forEach((el) => io.observe(el));
    }

    initSubPageAnimations() {
        // 서브 페이지 Hero 애니메이션 초기화
        this.subPage.hero();

        // GlobalTransportPage 초기화 (transport-module-section이 있는 경우)
        if (document.getElementById("transport-module-section")) {
            this.globalTransportPage.init();
        }

        const refreshScrollSystems = () => {
            if (typeof ScrollTrigger !== "undefined") {
                try {
                    ScrollTrigger.refresh();
                } catch (e) {
                    // ignore
                }
            }
            if (typeof AOS !== "undefined" && AOS.refresh) {
                try {
                    if (AOS.refreshHard) AOS.refreshHard();
                    else AOS.refresh();
                } catch (e) {
                    // ignore
                }
            }
        };

        // Initial refresh (DOMContentLoaded)
        refreshScrollSystems();

        // Deep refresh / scroll-restoration cases: refresh again after layout settles
        window.addEventListener("load", () => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    refreshScrollSystems();
                    setTimeout(refreshScrollSystems, 250);
                });
            });
        });

        // BFCache / scroll position restore can also break pin positions
        window.addEventListener("pageshow", () => {
            setTimeout(refreshScrollSystems, 50);
        });
    }

    initMainAnimations() {
        // 섹션 순서대로 초기화 (hero → advantages → ...)
        // Hero 섹션 클립패스 애니메이션 초기화
        this.mainAnimation.hero();

        // Advantages 섹션 스크롤 트리거 초기화 (solutions 이전에 위치)
        this.mainAnimation.advantage();

        // Our Partners(클라이언트 로고) Swiper 초기화
        this.mainAnimation.clientsLogos();

        // Global Network 섹션 애니메이션 초기화
        this.mainAnimation.network();

        // Logo Marquee 초기화
        this.mainAnimation.logoMarquee();

        // 모든 ScrollTrigger 초기화 후 refresh
        if (typeof ScrollTrigger !== "undefined") {
            ScrollTrigger.refresh();
        }
    }

    initLenis() {
        if (typeof window.Lenis !== "undefined") {
            const lenis = new window.Lenis();

            lenis.on("scroll", () => {
                if (typeof ScrollTrigger !== "undefined") {
                    ScrollTrigger.update();
                }
            });

            if (typeof gsap !== "undefined") {
                gsap.ticker.add((time) => {
                    lenis.raf(time * 1000);
                });
                gsap.ticker.lagSmoothing(0);
            }
        }
    }
}
